import { Core } from "@faire/design-tokens";
import { trackProductInfoClickOnImageInMediaGridClick } from "@faire/web-api/events/productInfo/click/clickOnImageInMediaGrid";
import { IImage } from "@faire/web-api/indigofair/data/IImage";
import { IVideo } from "@faire/web-api/indigofair/data/IVideo";
import { HistoryTiming } from "@faire/web/common/performance/HistoryTiming";
import { useRenderTracing } from "@faire/web/common/tracing/useRenderTracing";
import { Button } from "@faire/web/slate/Button";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { useViewport } from "@faire/web/ui/hooks/useViewport";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import { observer } from "mobx-react";
import React, { Suspense, useEffect, useRef, useState } from "react";
import styled, { css } from "styled-components";

import { AspectRatioPicture } from "@faire/retailer/components/Image/AspectRatioPicture";
import { VERTICAL_ASPECT_RATIO } from "@faire/retailer/lib/verticalAspectRatio";
import {
  ImageGridView,
  ShowAllContainer,
} from "@faire/retailer/views/product/__internal__/styled";
import { trackPreloadedImageGrid } from "@faire/retailer/views/product/preload/trackPreloadedImageGrid";
import {
  buildProductImageGridSrcSet,
  IMAGE_GRID_MAX_IMAGES_VISIBLE,
  IShowroomProductMedia,
} from "@faire/retailer/views/product/ProductImages/utils";
import { ShowroomProductData } from "@faire/retailer/views/product/ProductState";
import { LazyVideoPlayer } from "@faire/retailer/views/video/components/VideoPlayer";

interface IProps {
  openImageGallery: () => void | undefined;
  setIndex: (index: number) => void;
  alt: string;
  showVerticalImagery: boolean;
  productMedia: IShowroomProductMedia[];
  showroomData: ShowroomProductData | undefined;
  brandIsApparel: boolean | undefined;
}

const ShowAll: React.FC<
  Pick<IProps, "productMedia"> & { onClick: () => void }
> = ({ productMedia, onClick }) => {
  if (productMedia.length <= IMAGE_GRID_MAX_IMAGES_VISIBLE) {
    return null;
  }
  return (
    <ShowAllContainer data-test-id="show-all-media-button">
      <Button variant="secondary" onClick={onClick}>
        <StrictLocalMsg
          defaultMessage="Show All {imageTotalNumber} Photos"
          description={{
            text: "button to show all images available for viewing",
          }}
          values={{
            imageTotalNumber: productMedia.length,
          }}
        />
      </Button>
    </ShowAllContainer>
  );
};

const VideoMedia: React.FC<{
  video: IVideo;
  index: number;
  showVerticalImagery: boolean;
  onLoadedMetadata?: () => void;
}> = ({ video, index, showVerticalImagery, onLoadedMetadata }) => {
  return (
    <MediaContainer
      $index={index}
      key={video.url}
      $ratio={showVerticalImagery ? VERTICAL_ASPECT_RATIO : 1}
    >
      <Suspense fallback={null}>
        <LazyVideoPlayer
          video={video}
          videoOptions={{ autoplay: true, muted: true, loop: true }}
          onLoadedMetaData={onLoadedMetadata}
        />
      </Suspense>
    </MediaContainer>
  );
};
const ImageMedia: React.FC<
  Pick<IProps, "alt" | "showVerticalImagery"> & {
    image: IImage;
    index: number;
    onClick: (index: number) => void;
    onLoad?: () => void;
  }
> = ({ image, index, alt, showVerticalImagery, onClick, onLoad }) => {
  const { isTabletAndAbove } = useViewport();
  const { src, srcSet } = buildProductImageGridSrcSet(
    image,
    showVerticalImagery,
    isTabletAndAbove
  );

  return (
    <MediaContainer
      $index={index}
      key={src}
      $ratio={showVerticalImagery ? VERTICAL_ASPECT_RATIO : 1}
    >
      <StyledImage
        onClick={() => onClick(index)}
        $hideBorder
        $ratio={showVerticalImagery ? VERTICAL_ASPECT_RATIO : 1}
        $width="100%"
      >
        <img srcSet={srcSet} src={src} alt={alt} onLoad={onLoad} />
      </StyledImage>
    </MediaContainer>
  );
};

const ImageGridFn: React.FC<IProps> = ({
  setIndex,
  openImageGallery,
  productMedia,
  showVerticalImagery,
  showroomData,
  brandIsApparel,
  alt,
}) => {
  const showAllClick = () => {
    setIndex(0);
    openImageGallery();
  };
  const handleClick = (index: number) => {
    trackProductInfoClickOnImageInMediaGridClick(
      index.toString(),
      productMedia.length.toString()
    );
    setIndex(index);
    openImageGallery();
  };

  const hasMedia = productMedia.length > 0;
  const imageGridRef = useRef<HTMLDivElement>(null);
  const mediaToShow = productMedia.slice(0, IMAGE_GRID_MAX_IMAGES_VISIBLE);

  const [loadedCount, setLoadedCount] = useState(0);

  const endTrace = useRenderTracing(`ImageGrid`);

  useEffect(() => {
    if (loadedCount >= mediaToShow.length) {
      endTrace();
    }
  }, [loadedCount, mediaToShow.length, endTrace]);

  useEffect(() => {
    if (
      hasMedia &&
      imageGridRef.current &&
      !HistoryTiming.get().isInitialLoad
    ) {
      trackPreloadedImageGrid(
        imageGridRef.current,
        showroomData?.showVerticalImagery || false,
        brandIsApparel || false
      );
    }
  }, [hasMedia, showroomData?.showVerticalImagery, brandIsApparel]);

  if (!hasMedia) {
    return null;
  }
  const onLoad = () => {
    setLoadedCount((prevCount) => prevCount + 1);
  };
  return (
    <ImageGridView
      ref={imageGridRef}
      $mediaTotal={mediaToShow.length}
      data-test-id="image-section"
    >
      {mediaToShow.map((media, index) => {
        if (media.isVideo) {
          return (
            <React.Fragment key={index}>
              <VideoMedia
                video={media.video}
                index={media.index}
                showVerticalImagery={showVerticalImagery}
                onLoadedMetadata={onLoad}
              />
            </React.Fragment>
          );
        } else {
          return (
            <React.Fragment key={index}>
              <ImageMedia
                image={media.image}
                index={media.index}
                onClick={handleClick}
                showVerticalImagery={showVerticalImagery}
                alt={alt}
                onLoad={onLoad}
              />
            </React.Fragment>
          );
        }
      })}
      <ShowAll productMedia={productMedia} onClick={showAllClick} />
    </ImageGridView>
  );
};

export const ImageGrid = observer(ImageGridFn);

const MediaContainer = styled.div<{
  $index: number;
  $ratio: number;
}>`
  grid-area: ${(props) => css`media-${props.$index}`};
  overflow: hidden;
  ${responsiveMediaQueries.tabletAndAbove} {
    aspect-ratio: ${(props) => props.$ratio};
  }
  filter: brightness(98%);
  background-color: ${Core.surface.inverse};

  ${responsiveMediaQueries.mobile} {
    display: inline-block;
    width: inherit;
    height: 100vw;
  }
`;

const StyledImage = styled(AspectRatioPicture)`
  cursor: zoom-in;
  transition: transform 0.5s;
  height: 100%;

  ${responsiveMediaQueries.tabletAndAbove} {
    &:hover {
      transform: scale(1.05);
    }
  }
`;
